import type { AuthIdentityPolicy } from '../model/auth.types';
import {
  getRegisterContinueRequirement,
  getPreviousRegisterStep,
  getRegisterProgressRequirement,
  registrationSteps,
  type RegisterContinueRequirement,
  type RegisterStep,
} from '../model/registration.model';
import type { RegisterValues } from './register.types';

export const CONFIRMATION_CODE_LENGTH = 6;
export type RegisterStepValidationRules = Pick<
  AuthIdentityPolicy,
  | 'parseEmail'
  | 'isEmail'
  | 'parseInstagram'
  | 'parseTiktok'
  | 'parseYoutubeChannel'
  | 'parsePhone'
>;

const hasName = (value: string): boolean => value.trim().length > 0;

export const isEmailValid = (
  email: string,
  rules: Pick<RegisterStepValidationRules, 'isEmail'>,
): boolean => rules.isEmail(email);

const hasAtLeastOneSocial = (
  values: RegisterValues,
  rules: Pick<
    RegisterStepValidationRules,
    'parseInstagram' | 'parseTiktok' | 'parseYoutubeChannel'
  >,
): boolean => {
  return (
    rules.parseInstagram(values.instagram) !== null ||
    rules.parseTiktok(values.tiktok) !== null ||
    rules.parseYoutubeChannel(values.youtube) !== null
  );
};

const getStepIndex = (step: RegisterStep): number => {
  return registrationSteps.findIndex((stepConfig) => stepConfig.step === step);
};

type ContinueRequirementValidator = (
  values: RegisterValues,
  rules: RegisterStepValidationRules,
) => boolean;

const CONTINUE_REQUIREMENT_VALIDATORS: Record<
  RegisterContinueRequirement,
  ContinueRequirementValidator
> = {
  'valid-email': (values, rules) => isEmailValid(values.email, rules),
  'required-full-name': (values) =>
    hasName(values.firstName) && hasName(values.lastName),
  'at-least-one-social': (values, rules) => hasAtLeastOneSocial(values, rules),
  'optional-valid-phone': (values, rules) => {
    const phoneDigits = values.phoneNumber.replace(/\D/g, '');
    if (!phoneDigits) {
      return true;
    }

    return (
      rules.parsePhone(values.phoneCountryDialCode, values.phoneNumber) !== null
    );
  },
  'exact-confirmation-code': (values) =>
    values.code.trim().length === CONFIRMATION_CODE_LENGTH,
};

const isContinueRequirementSatisfied = (
  requirement: RegisterContinueRequirement,
  values: RegisterValues,
  rules: RegisterStepValidationRules,
): boolean => {
  return CONTINUE_REQUIREMENT_VALIDATORS[requirement](values, rules);
};

const isProgressRequirementSatisfied = (
  step: RegisterStep,
  values: RegisterValues,
  rules: RegisterStepValidationRules,
): boolean => {
  const progressRequirement = getRegisterProgressRequirement(step);
  if (!progressRequirement) {
    return true;
  }

  return isContinueRequirementSatisfied(progressRequirement, values, rules);
};

const getLastSignupStep = (): RegisterStep => {
  const lastStepConfig = registrationSteps.at(-1);
  return lastStepConfig ? lastStepConfig.step : 'email';
};

const getMaxReachableStep = (
  values: RegisterValues,
  rules: RegisterStepValidationRules,
): RegisterStep => {
  for (const stepConfig of registrationSteps) {
    if (!isProgressRequirementSatisfied(stepConfig.step, values, rules)) {
      return stepConfig.step;
    }
  }

  return getLastSignupStep();
};

export const constrainStepByProgress = (
  requestedStep: RegisterStep,
  values: RegisterValues,
  rules: RegisterStepValidationRules,
): RegisterStep => {
  const maxReachableStep = getMaxReachableStep(values, rules);
  return getStepIndex(requestedStep) <= getStepIndex(maxReachableStep)
    ? requestedStep
    : maxReachableStep;
};

export const canContinueFromStep = (
  step: RegisterStep,
  values: RegisterValues,
  rules: RegisterStepValidationRules,
): boolean => {
  const continueRequirement = getRegisterContinueRequirement(step);
  return isContinueRequirementSatisfied(continueRequirement, values, rules);
};

export { getPreviousRegisterStep };
